/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import './prototipo.css';
import {
  getExecucoesAtivas,
  getExecucaoResponsaveis,
  getResumo,
  listarVinculosBots,
  IResponsaveisPorEmpresa,
  STALE_TEMPO_REAL,
} from './api';
import { C, FONTE_MONO, FONTE_TEXTO } from './paleta';
import { horarioDosDados } from './utils/cacheTempoReal';
import { BotaoAtualizarAgora } from './components/BotaoAtualizarAgora';
import { SLOT_DO_TOUR } from './components/TourGuiado';
import { Icone, MATIZ_DA_ABA, VIDRO } from './components/Vidro';
import { AnaliseTab } from './tabs/AnaliseTab';
import { ROTULO_DO_STATUS } from './utils/exportarFluxo';
import { ProcessosTab } from './tabs/ProcessosTab';
import { ResumoTab, FiltrosResumoExtras } from './tabs/ResumoTab';
import { ErrosTab } from './tabs/ErrosTab';
import { DependenciasTab } from './tabs/DependenciasTab';
import {
  aoEscolherEmpresa,
  ComandoProcessos,
  filtrosIniciais,
  FiltrosCompartilhados,
} from './utils/filtrosCompartilhados';
import {
  derivarEmpresasComRegime,
  derivarEmpresasDaPagina,
  filtrarEmpresasResumo,
  FILTROS_RESUMO_VAZIOS,
  headlineDependencias,
  headlineProcessos,
  headlineResumo,
} from './utils/derivarResumo';
import { paraParams, PresetPeriodo, rangeDoPreset } from './utils/periodoProcessos';
import { DadosHeadlineErros, headlineErros } from './utils/auditoriaErros';
import { AlvoAuditoria, queryDoAlvo } from './utils/erroDoPopup';
import { useApelidos } from './useApelidos';

/**
 * Página /orquestrador — reconstrução FIEL do protótipo `sistema/index.html` (base
 * literal, decisão de 03/09: "quero exatamente como está no protótipo"): header com
 * eyebrow de competência, h1 "Dashboard", headline calculada e seletor de período
 * (Hoje/7/30 dias + intervalo livre), barra de abas com contadores mono e tabHint,
 * e as abas Processos / Resumo / Dependências espelhando o DOM do protótipo 1:1
 * (a aba Erros, de 09/09, é posterior ao protótipo — auditoria de ocorrências
 * erro/warning na mesma linguagem visual).
 * A topbar do protótipo (breadcrumb + "+ Nova automação") saiu a pedido de 04/09 —
 * era decorativa e redundante com o chrome do admin.
 *
 * Exceções ÚNICAS de contrato: a aba "Cadastro de Empresas" não entra (tb_empresa é
 * mantida por outro fluxo), os mocks não entram (dados via `api.ts`, estado vazio
 * honesto onde o backend ainda não fornece), e o shell do admin permanece em volta.
 *
 * A página é dona do estado transversal do protótipo: filtros compartilhados
 * (período/regime/empresa + grupo/bot/responsável/área) e o comando de navegação
 * cruzada Resumo → Processos. O `codigo: 'orquestrador-dashboard'` e o path
 * `/orquestrador` NÃO mudam (chave de permissão em tb_tela).
 */

/**
 * ORDEM da barra de abas. `analise` vem ANTES de `processos` (pedido 17/09: "para
 * os gráficos, precisamos criar uma aba nova, antes de processos").
 *
 * A ordem aqui NÃO define a aba padrão: o fallback abaixo continua sendo
 * `processos`, porque quem abre o dashboard para trabalhar cai no operacional.
 */
const TAB_KEYS = ['analise', 'processos', 'resumo', 'erros', 'dependencias'] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TAB_LABELS: Record<TabKey, string> = {
  analise: 'Análise',
  processos: 'Processos',
  resumo: 'Resumo',
  erros: 'Erros',
  dependencias: 'Dependências',
};

/**
 * UNIDADE de cada aba — fonte ÚNICA (achado 1 da auditoria de incongruências,
 * 09/09). Os quatro contadores da barra de abas aparecem lado a lado, na mesma
 * tipografia mono e sem nenhum rótulo: "14 · 312 · 108 · 14" lia-se como uma
 * série comparável, quando na verdade são bots, empresas e ocorrências. Foi o que
 * fez o "108" de Erros parecer contradizer o "35 empresas travadas" do nó de
 * Processos (unidades diferentes, ambos certos).
 *
 * `singular`/`plural` e `hint` moram juntos, mas descrevem coisas DIFERENTES de
 * propósito — não são duas formas de dizer a mesma frase:
 * - `singular`/`plural` nomeiam a unidade do CONTADOR (o número da barra, no
 *   `title` de TODAS as abas, inclusive as inativas): em Processos o número é de
 *   BOTS, então o title diz "14 bots";
 * - `hint` (frase longa, só da aba ATIVA) descreve o CONTEÚDO da aba — o recorte
 *   com que ela olha a esteira. Em Processos esse recorte é a ETAPA, que não é o
 *   que o contador conta, e a frase precisa dizer isso sem se passar pela unidade
 *   do número.
 * Ficam no mesmo registro para serem revisados juntos quando uma aba muda, não
 * porque devam repetir a mesma palavra.
 */
interface UnidadeDaAba {
  /** Singular/plural para o `title` do contador: "1 bot" / "14 bots". */
  singular: string;
  plural: string;
  /** Frase completa exibida à direita da barra, só para a aba ativa. */
  hint: string;
}

const TAB_UNIDADES: Record<TabKey, UnidadeDaAba> = {
  analise: {
    // A aba não tem contador (ver `contadores`): ela não conta uma coleção, ela
    // mede o processo. Singular/plural ficam preenchidos porque o tipo exige, mas
    // `titleDoContador` nunca os usa aqui — contador vazio não gera title.
    singular: 'etapa',
    plural: 'etapas',
    hint: 'O que cada bot concluiu em cada dia, quem rende mais, os gaps e o volume por analista.',
  },
  processos: {
    singular: 'bot',
    plural: 'bots',
    // O contador conta BOTS; a aba os organiza por ETAPA. A frase diz o recorte
    // sem chamá-lo de "a unidade", que aqui pertence ao número ao lado.
    hint: 'Cada bot posicionado na etapa da esteira: onde o trabalho está parando.',
  },
  resumo: {
    singular: 'empresa',
    plural: 'empresas',
    hint: 'A unidade é a empresa: situação, posição e avisos num único lugar.',
  },
  erros: {
    singular: 'ocorrência',
    plural: 'ocorrências',
    hint: 'A unidade é a ocorrência: qual bot quebrou, em qual empresa e em que etapa.',
  },
  dependencias: {
    singular: 'bot',
    plural: 'bots',
    hint: 'A unidade é o bot: de qual outro ele precisa terminar para começar.',
  },
};

/**
 * Ressalva do contador da aba Resumo (achado 6): ele descreve o recorte GLOBAL da
 * página, e o rodapé da tabela descreve o conjunto LISTADO. Divergem de propósito
 * quando a situação cadastral sai de "Ativas" ou quando o "Ver todas as empresas"
 * ignora o período — sem explicar, os dois números parecem se contradizer.
 */
const RESSALVA_CONTADOR_RESUMO =
  'no recorte da página (período + filtros); o rodapé da tabela mostra o conjunto listado, que pode diferir com "ver todas" ou outra situação cadastral';

/**
 * `title` do contador de uma aba: número + unidade explícita. Contador vazio (aba
 * ainda não carregou) fica SEM title — tooltip de "" seria só ruído.
 */
export const titleDoContador = (chave: TabKey, valor: string): string | undefined => {
  if (!valor) return undefined;
  const { singular, plural } = TAB_UNIDADES[chave];
  const base = `${valor} ${valor === '1' ? singular : plural}`;
  return chave === 'resumo' ? `${base} ${RESSALVA_CONTADOR_RESUMO}.` : base;
};

export const OrquestradorDashboardPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtros, setFiltros] = useState<FiltrosCompartilhados>(filtrosIniciais);
  const [extras, setExtras] = useState<FiltrosResumoExtras>({
    grupoEmpresa: null,
    bot: null,
    responsavel: null,
    area: null,
  });
  const [comando, setComando] = useState<ComandoProcessos | null>(null);
  // Último recorte carregado pela aba Erros (total + top tipo/bot) — alimenta a
  // headline calculada e o contador da aba; null enquanto a aba não carregou.
  const [errosDados, setErrosDados] = useState<DadosHeadlineErros | null>(null);

  const tabParam = searchParams.get('tab') as TabKey | null;
  const tab: TabKey = tabParam && TAB_KEYS.includes(tabParam) ? tabParam : 'processos';

  const params = useMemo(() => paraParams(filtros.periodo), [filtros.periodo]);
  const chavePeriodo = `${filtros.periodo.de}|${filtros.periodo.ate}`;

  // ── Queries transversais (mesmas chaves das abas → um cache só) ────────────
  const { data: vinculos } = useQuery({
    queryKey: ['orqVinculosBots'],
    queryFn: () => listarVinculosBots(),
  });
  const { data: resumo } = useQuery({
    queryKey: ['orqResumoProcessos', chavePeriodo],
    queryFn: () => getResumo(params),
    staleTime: STALE_TEMPO_REAL,
  });
  const { data: ativas } = useQuery({
    queryKey: ['orqExecucoesAtivasV2'],
    queryFn: () => getExecucoesAtivas(),
    refetchInterval: 30000,
    retry: 1,
  });
  // Mapa codi_emp → {área: responsável} declarado PELO BOT nas execuções ativas.
  const idsAtivas = useMemo(() => (ativas ?? []).map(a => a.exec_id).sort(), [ativas]);
  const { data: responsaveis } = useQuery({
    queryKey: ['orqResponsaveis', idsAtivas.join('|')],
    queryFn: async (): Promise<IResponsaveisPorEmpresa> => {
      const mapas = await Promise.all(
        idsAtivas.map(id =>
          getExecucaoResponsaveis(id).catch(() => ({}) as IResponsaveisPorEmpresa),
        ),
      );
      const junto: IResponsaveisPorEmpresa = {};
      mapas.forEach(mapa => {
        Object.entries(mapa).forEach(([codi, porArea]) => {
          junto[codi] = { ...junto[codi], ...porArea };
        });
      });
      return junto;
    },
    enabled: idsAtivas.length > 0,
    staleTime: 60000,
  });

  // Exclusão GLOBAL default (regra de negócio 08/09): empresa inativa/fora da
  // janela de vigência não aparece em LUGAR NENHUM por default — o corte é feito
  // AQUI, na fonte, para tudo que deriva de `empresas`/`empresasFiltradas` nunca
  // vê-las: Processos (select "Todas as empresas", board das sentinelas, fila,
  // popups), recorte global, headlines e contadores de aba. A ÚNICA exceção é o
  // filtro de SITUAÇÃO CADASTRAL da aba Resumo (pedido 09/09), que consome o
  // `empresasComInativas` logo abaixo (mesmo período, SEM a exclusão global, com as
  // flags do BFF) e revela as inativas com rótulo. O "Ver todas as empresas" do
  // banner NÃO revela mais inativas — ele só ignora o recorte de PERÍODO.
  //
  // Regime por nível (correção M1 da revisão 08/09): o recorte pelo regime
  // PRÓPRIO do bot também entra AQUI, na fonte — filtros de status/bot do
  // Resumo, situação/KPI e headlines derivam da MESMA rota filtrada que o
  // trilho exibe (bot oculto não conta em nada). Sem regime configurado,
  // o recorte devolve a mesma referência: zero mudança. A composição
  // exclusão-global → recorte-de-regime é a função PURA `derivarEmpresasDaPagina`
  // (utils/derivarResumo), pinada por teste — aqui só a memoização.
  const empresas = useMemo(
    () => derivarEmpresasDaPagina(resumo?.empresas ?? [], vinculos ?? []),
    [resumo, vinculos],
  );

  // MESMO dataset do período, SEM a exclusão global — só o recorte de regime
  // (pedido 09/09). Alimenta EXCLUSIVAMENTE o filtro de situação cadastral da aba
  // Resumo, que é o único lugar autorizado a mostrar empresa inativa/fora da
  // vigência; nenhuma outra aba recebe esta lista. Com o filtro em "Ativas"
  // (default), `filtrarPorSituacaoCadastral` devolve exatamente `empresas`.
  const empresasComInativas = useMemo(
    () => derivarEmpresasComRegime(resumo?.empresas ?? [], vinculos ?? []),
    [resumo, vinculos],
  );

  // Recorte global (o nomesAtivos() do protótipo): período + todos os filtros, sem
  // busca/situação (que são só da tabela do Resumo). O filtro de STATUS entra aqui
  // com a semântica do Resumo (ALGUM bot da rota num status selecionado) — em
  // Processos cada lista reaplica o recorte pelo status NAQUELE contexto.
  const empresasFiltradas = useMemo(
    () =>
      filtrarEmpresasResumo(
        empresas,
        {
          ...FILTROS_RESUMO_VAZIOS,
          regime: filtros.regime,
          empresa: filtros.empresa,
          status: filtros.status,
          grupoEmpresa: extras.grupoEmpresa,
          bot: extras.bot,
          responsavel: extras.responsavel,
          area: extras.area,
        },
        responsaveis,
      ),
    [empresas, filtros.regime, filtros.empresa, filtros.status, extras, responsaveis],
  );
  const filtroAtivo = Boolean(
    filtros.regime ||
      filtros.empresa ||
      filtros.status.length ||
      extras.grupoEmpresa ||
      extras.bot ||
      extras.responsavel ||
      extras.area,
  );
  // Os filtros em texto, para a Análise dizer que está no mesmo recorte do Processos.
  const descricaoDosFiltros = useMemo(
    () =>
      [
        filtros.regime && `regime ${filtros.regime}`,
        filtros.empresa && `empresa ${filtros.empresa}`,
        filtros.status.length &&
          `status ${filtros.status.map(st => ROTULO_DO_STATUS[st]).join(', ')}`,
        extras.grupoEmpresa && `grupo ${extras.grupoEmpresa}`,
        extras.bot && `bot ${extras.bot}`,
        extras.responsavel && `responsável ${extras.responsavel}`,
        extras.area && `área ${extras.area}`,
      ].filter((x): x is string => Boolean(x)),
    [filtros.regime, filtros.empresa, filtros.status, extras],
  );

  // Apelido de exibição dos bots (mesma query `orqVinculosBots` — sem fetch extra).
  const { apelidoDoBot } = useApelidos();

  // ── Headline calculada por aba (nunca literal — §6.1 da spec) ──────────────
  const headline = useMemo(() => {
    // A aba Análise tem headline própria e estática: as outras resumem um recorte
    // de empresas, e esta descreve o processo inteiro no tempo.
    if (tab === 'analise') return 'Evolução, gargalo e comparação entre datas';
    if (tab === 'resumo') return headlineResumo(empresasFiltradas);
    if (tab === 'erros') return headlineErros(errosDados);
    if (tab === 'dependencias') return headlineDependencias(vinculos ?? []);
    // O gargalo é agrupado pelo nome REAL; só a EXIBIÇÃO usa o apelido.
    return headlineProcessos(empresasFiltradas, apelidoDoBot);
  }, [tab, empresasFiltradas, vinculos, apelidoDoBot, errosDados]);

  // Contadores mono das abas — calculados dos mesmos dados que as abas mostram.
  const nBots = vinculos ? String(vinculos.length) : '';
  const contadores: Record<TabKey, string> = {
    // SEM contador de propósito. Os contadores da barra já foram fonte de confusão
    // por comparar unidades diferentes lado a lado (achado 1 da auditoria 09/09);
    // esta aba não tem uma coleção para contar — o número que importa nela (o
    // universo) já aparece dentro, com o aviso de que é o de hoje.
    analise: '',
    processos: nBots,
    // O contador da aba descreve o recorte GLOBAL da página (período + filtros
    // compartilhados, sempre sob a exclusão global das inativas) — não o conteúdo
    // interno da aba. Por isso ele NÃO acompanha o filtro de situação cadastral nem
    // o "Ver todas as empresas", ambos estado LOCAL do Resumo: nos estados
    // "Empresas inativas"/"Ativas e inativas" ele diverge do rodapé da tabela, e
    // está certo assim (comportamento deliberado, revisão 09/09 — B3). Quem precisa
    // do número do conjunto listado lê o rodapé "Mostrando N–M de T empresas" — e o
    // `title` do contador (RESSALVA_CONTADOR_RESUMO) agora diz isso na tela, em vez
    // de a divergência viver só neste comentário (achado 6 da auditoria 09/09).
    resumo: resumo ? String(empresasFiltradas.length) : '',
    erros: errosDados ? String(errosDados.total) : '',
    dependencias: nBots,
  };

  // Eyebrow de competência: a competência corrente REAL (o "06/2026" do protótipo era
  // texto estático de mock).
  const competencia = useMemo(() => {
    const agora = new Date();
    return `Competência ${String(agora.getMonth() + 1).padStart(2, '0')}/${agora.getFullYear()} · em tempo real`;
  }, []);

  const setTab = useCallback((nova: TabKey) => setSearchParams({ tab: nova }), [setSearchParams]);

  // ── Navegação cruzada Resumo → Processos ───────────────────────────────────
  const irParaProcessos = useCallback(
    (codiEmp: string, regime: string | null) => {
      setFiltros(atual => aoEscolherEmpresa(atual, codiEmp, regime));
      setComando({ tipo: 'macro' });
      setSearchParams({ tab: 'processos' });
    },
    [setSearchParams],
  );

  const irParaNano = useCallback(
    (bot: string, codiEmp: string, regime: string | null) => {
      setFiltros(atual => aoEscolherEmpresa(atual, codiEmp, regime));
      setComando({ tipo: 'nano', bot });
      setSearchParams({ tab: 'processos' });
    },
    [setSearchParams],
  );

  // ── Navegação cruzada Popup (mensagem de erro/aviso) → Erros ───────────────
  // Vai INTEIRA pela query string (o mesmo canal do `?tab=`): a URL resultante é
  // compartilhável e recarregável, e a ErrosTab inicializa os filtros a partir dela.
  const irParaErros = useCallback(
    (alvo: AlvoAuditoria) => setSearchParams(queryDoAlvo(alvo)),
    [setSearchParams],
  );

  const consumirComando = useCallback(() => setComando(null), []);

  const escolherPreset = (preset: PresetPeriodo) =>
    setFiltros(atual => ({ ...atual, periodo: rangeDoPreset(preset) }));

  // "30 dias" virou MES ATUAL / MES ANTERIOR em 25/09 (pedido do dono: "traga
  // apenas mes fechado"). Uma janela corrida de 30 dias atravessa a virada e
  // soma duas competencias no mesmo numero — no dia 25 ela ia de 26/08 a 25/09.
  // `7 dias` fica para quem precisa justamente atravessar a virada.
  const presets: Array<{ id: PresetPeriodo; label: string }> = [
    { id: 'hoje', label: 'Hoje' },
    { id: '7d', label: '7 dias' },
    { id: 'mes', label: 'Mês atual' },
    { id: 'mes_anterior', label: 'Mês anterior' },
  ];

  const matiz = MATIZ_DA_ABA[tab];

  return (
    <div
      className="orq-proto"
      style={{
        display: 'flex',
        minHeight: '100vh',
        fontFamily: FONTE_TEXTO,
        color: 'oklch(0.28 0.02 165)',
        background:
          `radial-gradient(1200px 500px at 0% -10%, oklch(0.93 0.05 ${matiz} / 0.55), transparent 60%), ` +
          'radial-gradient(1000px 500px at 100% 0%, oklch(0.94 0.04 235 / 0.5), transparent 60%), ' +
          'oklch(0.975 0.006 160)',
        transition: 'background 0.4s ease',
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            padding: 'clamp(16px, 2.2vw, 30px) clamp(14px, 2.4vw, 34px) 44px',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
            maxWidth: '1560px',
            width: '100%',
            boxSizing: 'border-box',
            margin: '0 auto',
          }}
        >
          {/*
            CABEÇALHO DE VIDRO (pedido 19/10: "refatoração completa desse menu").
            Título, competência e período num cartão só; abaixo, a barra de abas em
            pílulas, cada uma com o matiz da sua aba (`MATIZ_DA_ABA`).
          */}
          <div
            style={{
              ...VIDRO,
              borderRadius: '22px',
              padding: 'clamp(16px, 1.8vw, 24px) clamp(16px, 2vw, 28px)',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: '20px',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: FONTE_MONO,
                  fontSize: '10.5px',
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: `oklch(0.48 0.1 ${matiz})`,
                }}
              >
                <span
                  className="orq-pulso"
                  style={{
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    background: `oklch(0.62 0.15 ${matiz})`,
                  }}
                />
                <span>{competencia}</span>
              </div>
              <h1
                style={{
                  margin: 0,
                  fontSize: 'clamp(24px, 2.2vw, 30px)',
                  fontWeight: 700,
                  letterSpacing: '-0.025em',
                }}
              >
                Dashboard
              </h1>
              <div
                style={{
                  fontSize: '14px',
                  color: 'oklch(0.5 0.015 165)',
                  maxWidth: '720px',
                }}
              >
                {headline}
              </div>
            </div>
            {/*
              Seletor GLOBAL de período — some na aba Erros (achado 2 da auditoria
              09/09). Ele ficava visível e clicável ali sem afetar nada: a aba tem
              janela PRÓPRIA (24h/7d/31d), logo abaixo, e é ela que manda. Pior que
              inócuo, era contraditório: `rangeDoPreset` trava o início no dia 1 do
              mês (regra de COMPETÊNCIA do protótipo) e `rangeDoPresetAuditoria`
              não — no dia 3 do mês, "7 dias" valia 3 dias aqui e 7 lá.
              Esconder, e não sincronizar: as duas janelas medem coisas diferentes
              (competência de apuração × janela de log), então casá-las produziria
              um número errado nas duas abas em vez de um controle morto numa.
            */}
            {tab !== 'erros' && (
              <div
                data-testid="periodo-global"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: '3px',
                    padding: '3px',
                    borderRadius: '12px',
                    background: 'rgba(255,255,255,0.55)',
                    border: '1px solid rgba(255,255,255,0.9)',
                    boxShadow: 'inset 0 1px 2px rgba(16,60,48,0.07)',
                    fontSize: '13px',
                  }}
                >
                  {presets.map(p => {
                    const ativo = filtros.periodo.preset === p.id;
                    return (
                      <div
                        key={p.id}
                        onClick={() => escolherPreset(p.id)}
                        // O destaque do preset ativo vivia SÓ no estilo inline: nem
                        // teste nem leitor de tela conseguiam dizer qual estava
                        // escolhido. `data-ativo` expõe o estado; `aria-pressed` dá
                        // a semântica de botão de alternância a quem usa leitor.
                        data-ativo={ativo}
                        role="button"
                        aria-pressed={ativo}
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            escolherPreset(p.id);
                          }
                        }}
                        style={{
                          padding: '7px 14px',
                          borderRadius: '9px',
                          cursor: 'pointer',
                          background: ativo
                            ? `linear-gradient(140deg, oklch(0.36 0.05 ${matiz}), ${C.dark})`
                            : 'transparent',
                          color: ativo ? C.onDark : C.sub,
                          fontWeight: ativo ? 600 : 500,
                          boxShadow: ativo ? '0 4px 12px rgba(16,40,34,0.25)' : 'none',
                          transition: 'background 0.2s ease',
                        }}
                      >
                        {p.label}
                      </div>
                    );
                  })}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 10px',
                    borderRadius: '12px',
                    background: 'rgba(255,255,255,0.65)',
                    border: '1px solid rgba(255,255,255,0.9)',
                    color: C.sub,
                  }}
                >
                  <Icone nome="calendario" tamanho={15} />
                  <input
                    type="date"
                    value={filtros.periodo.de}
                    onChange={ev =>
                      setFiltros(atual => ({
                        ...atual,
                        periodo: { preset: null, de: ev.target.value, ate: atual.periodo.ate },
                      }))
                    }
                    style={{
                      border: 'none',
                      outline: 'none',
                      fontFamily: FONTE_TEXTO,
                      fontSize: '12.5px',
                      color: 'oklch(0.3 0.02 165)',
                      background: 'transparent',
                    }}
                  />
                  <span style={{ color: 'oklch(0.7 0.01 165)', fontSize: '12px' }}>→</span>
                  <input
                    type="date"
                    value={filtros.periodo.ate}
                    onChange={ev =>
                      setFiltros(atual => ({
                        ...atual,
                        periodo: { preset: null, de: atual.periodo.de, ate: ev.target.value },
                      }))
                    }
                    style={{
                      border: 'none',
                      outline: 'none',
                      fontFamily: FONTE_TEXTO,
                      fontSize: '12.5px',
                      color: 'oklch(0.3 0.02 165)',
                      background: 'transparent',
                    }}
                  />
                </div>
                <BotaoAtualizarAgora />
                {horarioDosDados(resumo?.calculado_em) && (
                  <span
                    data-testid="dados-das"
                    title="Os dados do monitoramento são renovados sozinhos a cada poucos segundos. Para buscar agora, use Atualizar agora."
                    style={{
                      fontFamily: FONTE_MONO,
                      fontSize: '11px',
                      color: 'oklch(0.5 0.02 165)',
                      cursor: 'help',
                    }}
                  >
                    Dados das {horarioDosDados(resumo?.calculado_em)}
                  </span>
                )}
                {/* Encaixe do "?" do tour guiado: a aba Processos é dona do tour e
                    desenha o botão AQUI por portal, ao lado do Atualizar (pedido do
                    dono, 13/10: "fica melhor do que ele flutuando"). */}
                <span id={SLOT_DO_TOUR} style={{ display: 'inline-flex' }} />
              </div>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '14px 24px',
              flexWrap: 'wrap',
            }}
          >
            <div
              role="tablist"
              style={{
                ...VIDRO,
                display: 'flex',
                gap: '4px',
                padding: '5px',
                borderRadius: '16px',
                overflowX: 'auto',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}
            >
              {TAB_KEYS.map(chave => {
                const ativa = chave === tab;
                const label = TAB_LABELS[chave];
                const h = MATIZ_DA_ABA[chave];
                let fundoDoContador = 'transparent';
                if (contadores[chave]) {
                  fundoDoContador = ativa ? 'rgba(255,255,255,0.22)' : `oklch(0.94 0.04 ${h})`;
                }
                return (
                  <div
                    key={chave}
                    role="tab"
                    aria-selected={ativa}
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        (e.currentTarget as HTMLElement).click();
                      }
                    }}
                    /*
                      CLICAR NA ABA JÁ ATIVA VOLTA AO PRIMEIRO NÍVEL (pedido 17/09):
                      "eu clico em algum grupo de bots... e quero voltar para o
                      primeiro nível, preciso ficar clicando em voltar página".

                      Só a aba Processos tem níveis (macro → board → nano), então só
                      ela reseta. E só quando JÁ está ativa: trocar de aba continua
                      sendo `setTab` puro, sem resetar nada — quem sai de Erros para
                      Processos espera encontrar a navegação como deixou.

                      `setComando` é o canal que já existia para isso (Resumo →
                      Processos usa o mesmo), consumido pelo `useEffect` da
                      ProcessosTab que zera grupo/bot/popup. O objeto é novo a cada
                      clique, então dois cliques seguidos funcionam: o efeito
                      redispara por identidade, não por valor.

                      `setTab(chave)` sozinho não serviria — a URL não muda e o
                      React Router não re-renderiza nada.
                    */
                    onClick={() => {
                      if (chave === 'processos' && tab === 'processos') {
                        setComando({ tipo: 'macro' });
                        return;
                      }
                      setTab(chave);
                    }}
                    style={{
                      padding: '9px 15px',
                      borderRadius: '12px',
                      fontSize: '13.5px',
                      fontWeight: ativa ? 650 : 500,
                      color: ativa ? 'white' : C.sub,
                      background: ativa
                        ? `linear-gradient(140deg, oklch(0.63 0.15 ${h}), oklch(0.5 0.15 ${h}))`
                        : 'transparent',
                      boxShadow: ativa ? `0 6px 18px oklch(0.55 0.15 ${h} / 0.32)` : 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      whiteSpace: 'nowrap',
                      transition: 'background 0.2s ease, color 0.2s ease',
                    }}
                  >
                    <Icone
                      nome={chave}
                      tamanho={16}
                      style={{ color: ativa ? 'white' : `oklch(0.55 0.13 ${h})` }}
                    />
                    <span>{label}</span>
                    {/* Unidade no `title` de TODOS os contadores (achado 1): os
                        quatro números ficam lado a lado na mesma tipografia, e
                        sem isso nada na tela diz que "108" são ocorrências e
                        "14" são bots. O hint da direita só cobre a aba ativa. */}
                    <span
                      title={titleDoContador(chave, contadores[chave])}
                      style={{
                        fontFamily: FONTE_MONO,
                        fontSize: '10.5px',
                        fontWeight: 600,
                        color: ativa ? 'white' : `oklch(0.45 0.1 ${h})`,
                        background: fundoDoContador,
                        padding: contadores[chave] ? '1px 7px' : 0,
                        borderRadius: '999px',
                      }}
                    >
                      {contadores[chave]}
                    </span>
                  </div>
                );
              })}
            </div>
            <div
              style={{
                fontSize: '12.5px',
                color: 'oklch(0.52 0.015 165)',
                maxWidth: '520px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  flex: 'none',
                  background: `oklch(0.6 0.14 ${matiz})`,
                }}
              />
              {TAB_UNIDADES[tab].hint}
            </div>
          </div>

          {tab === 'analise' && (
            <AnaliseTab
              params={params}
              chavePeriodo={chavePeriodo}
              periodoDe={filtros.periodo.de}
              empresas={empresas}
              empresasFiltradas={empresasFiltradas}
              filtroAtivo={filtroAtivo}
              descricaoDosFiltros={descricaoDosFiltros}
              responsaveis={responsaveis}
            />
          )}
          {tab === 'processos' && (
            <ProcessosTab
              filtros={filtros}
              onFiltrosChange={setFiltros}
              empresasFiltradas={empresasFiltradas}
              empresas={empresas}
              filtroAtivo={filtroAtivo}
              comando={comando}
              onComandoConsumido={consumirComando}
              onVerNaAuditoria={irParaErros}
            />
          )}
          {tab === 'resumo' && (
            <ResumoTab
              filtros={filtros}
              onFiltrosChange={setFiltros}
              extras={extras}
              onExtrasChange={setExtras}
              empresasComInativas={empresasComInativas}
              empresasFiltradas={empresasFiltradas}
              responsaveis={responsaveis}
              vinculos={vinculos}
              onIrParaProcessos={irParaProcessos}
              onIrParaNano={irParaNano}
              params={params}
              chavePeriodo={chavePeriodo}
            />
          )}
          {/* `key` pela query: chegar do popup com OUTRO recorte remonta a aba, que
              relê os filtros iniciais da URL (o estado dos filtros é interno dela). */}
          {tab === 'erros' && (
            <ErrosTab
              key={searchParams.toString()}
              onDadosChange={setErrosDados}
              inicial={searchParams}
              empresas={empresas}
            />
          )}
          {tab === 'dependencias' && <DependenciasTab />}
        </div>
      </div>
    </div>
  );
};

export default OrquestradorDashboardPage;
